import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { Button, Card, Dialog, ListRow } from '../../ui';
import { getMyClientRow, listBodyMetrics } from '../../shared/queries';
import { supabase } from '../../shared/supabase/client';
import { formatMoment } from '../../shared/formatDate';
import { text } from '../../shared/text';
import type { MessageId } from '../../shared/text';
import { OPTIONAL_CONSENTS, buildConsentRecord, consentName } from '../../shared/consent';
import type { ConsentParties, OptionalConsentId } from '../../shared/consent';
import { resolveLanguage } from '../../shared/language';
import { useInstance } from '../instance/InstanceProvider';
import { ClientGuard } from './ClientGuard';
import { AppBar, ClientLoading, ClientPage } from './ClientPage';
import { consentGivenAt, decide, hasConsent, useChoices } from './consentChoices';
import { ChevronRight } from './icons';
import {
  formatMeasurement,
  fromRows,
  latest,
  measurementKind,
  series,
  useDeviceMeasurements,
} from './measurements';

/**
 * The client's own standing with the product: who they are, who their coach is,
 * what has been agreed, what they can change, and how to get their data out or
 * have it deleted.
 *
 * ## What the hi-fi drew here, and why most of it is not on this screen
 *
 * `hifi/screens-account.jsx`'s `ProfileScreen` opens with a stats grid, and the
 * design export's own README records every item in it against the decision it
 * collides with. It is not ported, and these are not styling choices:
 *
 *   * **body fat, with a percentage and a direction arrow — refused.** Counsel's
 *     body-metrics ruling names it: no photos, no body fat, no BMI, no computed
 *     index, no categories, no coach-set targets, no ideal-weight suggestions. A
 *     computed classification of a body is the first step towards software that
 *     calculates for a medical purpose;
 *   * **resting heart rate — refused**, for the same reason. It is physiological
 *     data, and software that calculates or recommends from physiological data
 *     for a medical purpose can be a medical device;
 *   * **age as a number — not a free field.** It exists as one tick and no date
 *     of birth, which is `legal-review-at-the-gate`'s carve-out item 2. There is
 *     no age anywhere in this product to display;
 *   * **gender — deliberately never acquired.** The Castilian copy was routed
 *     around gendered adjectives precisely so the product would not pick up a
 *     gender field to solve a grammar problem. Adding one now, for display, would
 *     be worse than the grammar problem;
 *   * **weight with a "↘ −1.2" — the weight yes, the arrow no.** Metrics copy is
 *     neutral: "Último" and "Cambio", never a direction arrow, never a colour;
 *   * **a "Day 47" chip and a flame — parked by the owner**, streaks as a future
 *     idea and the day counter explicitly ignored.
 *
 * What survives is the identity block, the coach card, the client's own
 * measurements, and the settings rows — which is the screen worth building.
 *
 * ## No health section, and its absence is a requirement with a test
 *
 * This is the single most likely place in the product for an "health
 * information" section to reappear, because it is where a designer would
 * naturally put one. There is none, no field invites injuries, conditions or
 * medication, and a test asserts it rather than a convention discouraging it.
 *
 * ## What is hollow here
 *
 * **A consent the client changes does not reach the database.** `client` has no
 * `client_update_self` policy, so the client's own choices are held on the
 * device by `./consentChoices.ts` — which also means the coach's admin does not
 * learn about them, and another phone does not either. **An export request and a
 * deletion request reach nobody**, because the coach's side of both is not
 * built. Both are said on the screen and both are on the gate.
 */
export function MePage() {
  return (
    <ClientGuard>
      <Me />
    </ClientGuard>
  );
}

function Me() {
  const instance = useInstance();
  const choices = useChoices();
  const me = useQuery({ queryKey: ['my-client-row'], queryFn: () => getMyClientRow(supabase) });
  const stored = useQuery({ queryKey: ['body-metrics'], queryFn: () => listBodyMetrics(supabase) });
  const device = useDeviceMeasurements();

  const parties: ConsentParties = {
    coach: instance.coachDisplayName,
    role: instance.roleLine,
  };

  if (me.isPending) return <ClientLoading />;

  const row = me.data;
  const metricsOn = hasConsent('metrics', row, choices);
  const messagingOn = hasConsent('messaging', row, choices);
  const ackAt = consentGivenAt('ack', row, choices);

  const weight = metricsOn ? latest(series('weight', fromRows(stored.data ?? []), device)) : null;

  return (
    <ClientPage>
      <AppBar backTo={`/c/${instance.slug}/today`} backLabel={text('client.me.back')} />

      {/* The identity block. A name, an address, and since when. Nothing else. */}
      <h1 className="text-ink font-display mt-2 text-3xl leading-tight">
        {row?.display_name ?? text('client.me.title')}
      </h1>
      <p className="text-ink-muted mt-1 text-sm">{row?.email ?? ''}</p>
      {row?.first_signed_in_at == null ? null : (
        <p className="text-ink-muted mt-0.5 text-sm">
          {text('client.me.since', { date: formatMoment(row.first_signed_in_at) })}
        </p>
      )}

      {/* The coach card, and the route to her that survives every privacy choice. */}
      <Card className="mt-6">
        <p className="text-ink-muted text-xs tracking-wide uppercase">
          {text('client.me.coachTitle')}
        </p>
        <p className="text-ink font-display mt-1 text-xl">{text('client.me.coachLine', parties)}</p>
        {messagingOn ? (
          <Link
            to={`/c/${instance.slug}/messages`}
            className="text-primary mt-3 inline-flex min-h-11 items-center gap-1 text-sm font-medium underline-offset-2 hover:underline"
          >
            {text('client.me.coachWrite')}
            <ChevronRight size={18} />
          </Link>
        ) : (
          <p className="text-ink-soft mt-2 text-sm leading-relaxed">
            {text('client.me.coachNoMessages', { coach: parties.coach })}
          </p>
        )}
      </Card>

      {/*
        The measurements, and only for a client who opted in. A client who did
        not never sees the feature named at all — not as a disabled row, not as
        a locked one (`access-and-consent`: declining "simply hides that
        feature").
      */}
      {metricsOn ? (
        <section className="mt-8">
          <Link
            to={`/c/${instance.slug}/measurements`}
            className="block rounded-[--radius-control]"
          >
            <ListRow
              title={text('client.me.measurementsLink')}
              meta={
                weight === null
                  ? undefined
                  : `${text('client.measurements.latest')}: ${formatMeasurement(
                      weight.value,
                      measurementKind('weight').unit,
                    )}`
              }
              trailing={<ChevronRight size={20} className="text-ink-muted" />}
            />
          </Link>
        </section>
      ) : null}

      <Privacy ackAt={ackAt} parties={parties} />

      {/*
        The boundary, on the screen, without opening a further page — and she is
        described by the role line her instance row carries, never as a doctor.
      */}
      <section className="mt-8">
        <h2 className="text-ink font-display text-lg">{text('client.me.boundaryTitle')}</h2>
        <p className="text-ink-soft mt-2 text-sm leading-relaxed">
          {text('client.me.boundaryBody', parties)}
        </p>
      </section>

      <DataRights coach={parties.coach} />

      <p className="text-ink-muted mt-8 text-xs leading-relaxed">
        {text('client.me.onThisDevice')}
      </p>
    </ClientPage>
  );
}

/** Which sentence a client is shown before they turn one off. */
const WITHDRAWAL_WARNING: Record<OptionalConsentId, MessageId> = {
  metrics: 'client.me.withdrawMetrics',
  messaging: 'client.me.withdrawMessaging',
  hurt: 'client.me.withdrawHurt',
};

/**
 * What has been agreed, and the three switches.
 *
 * **Turning one off warns first.** The spec requires the client be told what
 * will be removed *before* they confirm, and the reason is the difference
 * between a privacy control a person can use and one they are afraid to touch:
 * nobody should discover their message history is gone by noticing it is gone.
 * Backing out removes nothing — `decide` is not called at all on that path.
 *
 * **Turning one back on returns the feature empty.** The withdrawal deleted what
 * was under it, immediately, so there is nothing left to come back. That is a
 * property of `./consentChoices.ts` rather than of this screen.
 */
function Privacy({ ackAt, parties }: { ackAt: string | null; parties: ConsentParties }) {
  const me = useQuery({ queryKey: ['my-client-row'], queryFn: () => getMyClientRow(supabase) });
  const choices = useChoices();
  const [confirming, setConfirming] = useState<OptionalConsentId | null>(null);
  const language = resolveLanguage();

  function record(consent: OptionalConsentId, decision: 'given' | 'withdrawn') {
    decide([buildConsentRecord({ consent, decision, parties, surface: 'profile', language })]);
  }

  return (
    <section className="mt-8">
      <h2 className="text-ink font-display text-lg">{text('client.me.privacyTitle')}</h2>
      <p className="text-ink-soft mt-1 text-sm leading-relaxed">{text('client.me.privacyIntro')}</p>

      <p className="text-ink-muted mt-3 text-sm">
        {ackAt === null
          ? text('client.me.ackPending')
          : text('client.me.ackGiven', { date: formatMoment(ackAt) })}
      </p>

      <ul className="mt-2">
        {OPTIONAL_CONSENTS.map((consent) => {
          const givenAt = consentGivenAt(consent, me.data, choices);
          const on = givenAt !== null;
          return (
            <li key={consent}>
              <ListRow
                title={consentName(consent)}
                meta={
                  givenAt === null
                    ? undefined
                    : text('client.me.consentSince', { date: formatMoment(givenAt) })
                }
                trailing={
                  <label
                    htmlFor={`switch-${consent}`}
                    className="flex min-h-11 cursor-pointer items-center gap-2"
                  >
                    <span className="text-ink-soft text-xs">
                      {on ? text('client.me.consentOn') : text('client.me.consentOff')}
                    </span>
                    <input
                      id={`switch-${consent}`}
                      type="checkbox"
                      checked={on}
                      aria-label={text('client.me.consentToggle', { name: consentName(consent) })}
                      onChange={() => {
                        // Off asks first; on is immediate. Giving something back
                        // needs no warning, and taking it away does.
                        if (on) setConfirming(consent);
                        else record(consent, 'given');
                      }}
                      className="accent-primary size-5"
                    />
                  </label>
                }
              />
            </li>
          );
        })}
      </ul>

      <Dialog
        open={confirming !== null}
        onClose={() => {
          setConfirming(null);
        }}
        title={
          confirming === null
            ? ''
            : text('client.me.withdrawTitle', { name: consentName(confirming) })
        }
      >
        <p className="text-ink-soft mt-2 text-sm leading-relaxed">
          {confirming === null ? '' : text(WITHDRAWAL_WARNING[confirming])}
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          <Button
            variant="danger"
            onClick={() => {
              if (confirming !== null) record(confirming, 'withdrawn');
              setConfirming(null);
            }}
          >
            {text('client.me.withdrawConfirm')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setConfirming(null);
            }}
          >
            {text('client.me.withdrawCancel')}
          </Button>
        </div>
      </Dialog>
    </section>
  );
}

/**
 * A copy of everything, and deletion.
 *
 * Both say what happens and by when — one month, which is what the law gives and
 * what a person can hold somebody to. Deletion asks for a confirmation because
 * it is irreversible; it does **not** ask for a reason, here or anywhere, and
 * there is no field on this screen in which one could be given.
 *
 * **Neither request reaches anybody in this version**, because there is no table
 * and no coach's side, and the screen says so beside each confirmation rather
 * than letting a client wait a month for something that was never sent. A data
 * right the product offers and cannot deliver is worse than one it does not
 * offer, which is why this is a gate line and not a backlog item.
 */
function DataRights({ coach }: { coach: string }) {
  const [asked, setAsked] = useState<'export' | 'delete' | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  return (
    <section className="mt-8">
      <h2 className="text-ink font-display text-lg">{text('client.me.rightsTitle')}</h2>

      <div className="mt-3 flex flex-wrap gap-3">
        <Button
          variant="secondary"
          onClick={() => {
            setAsked('export');
          }}
        >
          {text('client.me.exportAction')}
        </Button>
        <Button
          variant="danger-ghost"
          onClick={() => {
            setConfirmingDelete(true);
          }}
        >
          {text('client.me.deleteAction')}
        </Button>
      </div>

      {asked === null ? null : (
        <Card className="mt-4" role="status">
          <h3 className="text-ink font-display text-lg">
            {asked === 'export'
              ? text('client.me.exportDoneTitle')
              : text('client.me.deleteDoneTitle')}
          </h3>
          <p className="text-ink-soft mt-2 text-sm leading-relaxed">
            {asked === 'export'
              ? text('client.me.exportDoneBody')
              : text('client.me.deleteDoneBody')}
          </p>
          <p className="text-ink-muted mt-2 text-xs leading-relaxed">
            {text('client.me.requestNotDelivered', { coach })}
          </p>
        </Card>
      )}

      <Dialog
        open={confirmingDelete}
        onClose={() => {
          setConfirmingDelete(false);
        }}
        title={text('client.me.deleteTitle')}
      >
        <p className="text-ink-soft mt-2 text-sm leading-relaxed">{text('client.me.deleteBody')}</p>
        <div className="mt-5 flex flex-wrap gap-3">
          <Button
            variant="danger"
            onClick={() => {
              setAsked('delete');
              setConfirmingDelete(false);
            }}
          >
            {text('client.me.deleteConfirm')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setConfirmingDelete(false);
            }}
          >
            {text('client.me.deleteCancel')}
          </Button>
        </div>
      </Dialog>
    </section>
  );
}
